// chat-story 纯 SVG 渲染。复用 tick.chatReveal 推导已揭示消息数。
// 气泡布局：左/右对齐 + 估算气泡宽（CJK 1.0 系数）+ 文本换行。
// 自动翻屏：内容超屏时滚动偏移，最新消息始终在底部可见区。
import type { SceneData } from '../../api/types'
import type { ChatData } from './types'
import { chatReveal } from './tick'
import { COLORS, FONT_SANS, esc, wrapText, textBlock } from '../../export/svgRender'

const TOP = 200      // 内容区顶部（标题/分割线下方）
const BOTTOM = 1820  // 内容区底部（水印上方）
const AVAIL_H = BOTTOM - TOP

export function render(data: SceneData, time: number): string {
  const d = data as unknown as ChatData
  const { revealCount } = chatReveal(data, time)
  const messages = d.messages || []
  const participants = d.participants || []
  const count = Math.min(revealCount, messages.length)

  const parts: string[] = []
  parts.push(`  <text x="540" y="80" font-size="44" font-weight="700" fill="${COLORS.t2}" text-anchor="middle" dominant-baseline="middle" font-family="${FONT_SANS}">${esc(d.title)}</text>`)
  parts.push(`  <line x1="60" y1="130" x2="1020" y2="130" stroke="${COLORS.border}" stroke-width="2"/>`)

  // 1. 预计算所有已揭示消息的布局（y + 高度），不渲染
  interface Item {
    y: number; bubbleH: number; bubbleW: number; bx: number
    isLeft: boolean; name: string; lines: string[]; time: string; senderX: number
  }
  const layout: Item[] = []
  let cy = TOP
  for (let i = 0; i < count; i++) {
    const m = messages[i]
    const isLeft = m.sender === 0
    const name = (participants[m.sender] || '').split(' ')[0]
    const lines = wrapText(m.text || '', 42, 680)
    const bubbleW = Math.min(760, Math.max(1, ...lines.map((l) => l.length)) * 42 + 80)
    const bubbleH = lines.length * 42 * 1.4 + 56
    const bx = isLeft ? 60 : 1020 - bubbleW
    const senderX = isLeft ? 80 : 1000
    layout.push({ y: cy, bubbleH, bubbleW, bx, isLeft, name, lines, time: m.time, senderX })
    cy += 34 + bubbleH + 56
  }

  // 2. 滚动偏移：内容超屏时，最新消息（底部）始终可见
  const totalH = cy - TOP
  const yOffset = Math.max(0, totalH - AVAIL_H)

  // 3. 渲染可见消息（y 减去 yOffset，跳过画面外的）
  for (const item of layout) {
    const y = item.y - yOffset
    // 跳过完全在画面外的（上方溢出或下方溢出）
    if (y + item.bubbleH + 60 < TOP || y > BOTTOM) continue
    const anchor = item.isLeft ? 'start' : 'end'
    const textX = item.isLeft ? item.bx + 40 : item.bx + item.bubbleW - 40
    parts.push(`  <text x="${item.senderX}" y="${y + 14}" font-size="30" font-weight="600" fill="${COLORS.pHover}" text-anchor="${anchor}" dominant-baseline="middle" font-family="${FONT_SANS}">${esc(item.name)}</text>`)
    parts.push(`  <rect x="${item.bx}" y="${y + 34}" width="${item.bubbleW}" height="${item.bubbleH}" rx="36" ry="36" fill="${item.isLeft ? COLORS.bg3 : COLORS.p}"/>`)
    parts.push(textBlock(item.lines, textX, y + 34 + 28 + 19, 42, COLORS.w, { anchor, weight: 400, lineHeight: 1.4 }))
    parts.push(`  <text x="${item.senderX}" y="${y + 34 + item.bubbleH + 20}" font-size="24" font-weight="400" fill="${COLORS.t4}" text-anchor="${anchor}" dominant-baseline="middle" font-family="${FONT_SANS}">${esc(item.time)}</text>`)
  }

  // 4. typing indicator（下一条即将出现，也跟随滚动）
  if (revealCount < messages.length) {
    const m = messages[revealCount]
    const isLeft = m.sender === 0
    const name = (participants[m.sender] || '').split(' ')[0]
    const bubbleW = 160
    const bubbleH = 96
    const bx = isLeft ? 60 : 1020 - bubbleW
    const senderX = isLeft ? 80 : 1000
    const anchor = isLeft ? 'start' : 'end'
    const ty = cy - yOffset // typing 紧跟最后消息下方
    if (ty + bubbleH + 60 >= TOP && ty <= BOTTOM) {
      parts.push(`  <text x="${senderX}" y="${ty + 14}" font-size="30" font-weight="600" fill="${COLORS.pHover}" text-anchor="${anchor}" dominant-baseline="middle" font-family="${FONT_SANS}">${esc(name)}</text>`)
      parts.push(`  <rect x="${bx}" y="${ty + 34}" width="${bubbleW}" height="${bubbleH}" rx="36" ry="36" fill="${isLeft ? COLORS.bg3 : COLORS.p}"/>`)
      parts.push(`  <text x="${bx + bubbleW / 2}" y="${ty + 34 + bubbleH / 2}" font-size="44" fill="${COLORS.w}" text-anchor="middle" dominant-baseline="middle" font-family="${FONT_SANS}">···</text>`)
    }
  }

  return '\n' + parts.join('\n')
}
